import React, { useEffect, useMemo, useRef, useState } from "react";
import { fetchWrapper } from "utils/apiFetch";
import ShowToast from "components/ShowToast";
import "./PanReports.css";
import { useSession } from "hooks/useSession";
import { useNavigate } from "react-router";
import PanReceiptModal from "./component/PanReceipt";

/* ─────────────────────────────────────────────────────────────────────────
   Status → badge class + display label
   The backend returns whatever status string PanCardRequest currently has
   ("initiated", "accepted", "rejected", or legacy values like
   "Successfully Completed" / "Rejected"). Normalise loosely so the badge
   colour is always right regardless of which casing/wording is stored.
───────────────────────────────────────────────────────────────────────── */
const STATUS_META = {
    accepted:  { label: "Accepted", cls: "success" },
    success:   { label: "Accepted", cls: "success" },
    completed: { label: "Accepted", cls: "success" },
    rejected:  { label: "Rejected", cls: "danger" },
    initiated: { label: "Pending",  cls: "pending" },
    pending:   { label: "Pending",  cls: "pending" },
};

function statusMeta(raw) {
    const key = (raw || "").toString().trim().toLowerCase();
    if (key.includes("reject")) return STATUS_META.rejected;
    if (key.includes("accept") || key.includes("success") || key.includes("complet"))
        return STATUS_META.accepted;
    return STATUS_META.initiated;
}

const STATUS_FILTERS = [
    { value: "all",      label: "All statuses" },
    { value: "pending",  label: "Pending" },
    { value: "accepted", label: "Accepted" },
    { value: "rejected", label: "Rejected" },
];

/* ─────────────────────────────────────────────────────────────────────────
   Document availability for a record.
   The GET action already returns pdf_form93 / pdf_formcr / pdf_bundle /
   slip as full URLs (or null if not generated yet) — we just need to
   surface them. media-relative paths (older rows / non-URL fields) get
   prefixed with the API host so links still resolve.
───────────────────────────────────────────────────────────────────────── */
const MEDIA_BASE = "https://api.mokshiq.in";

function resolveUrl(value) {
    if (!value) return null;
    // Already absolute (http/https) — backend's field.url usually returns
    // this when MEDIA_URL is configured as a full URL, or behind some
    // storage backends (S3, etc).
    if (/^https?:\/\//i.test(value)) return value;
    // Root-relative path (Django's typical FileField.url, e.g.
    // "/media/pan_pdfs/form93_123.pdf") — just prefix the host, no
    // extra "/media/" segment needed since it's already in the path.
    if (value.startsWith("/")) return `${MEDIA_BASE}${value}`;
    // Bare filename / relative path (legacy str(field) values like
    // "pancard/photo/x.jpg") — needs the /media/ prefix added.
    return `${MEDIA_BASE}/media/${value}`;
}

function getDocuments(item) {
    return [
        { key: "form93", label: "Form 93 (PDF)",           url: resolveUrl(item.pdf_form93) },
        { key: "formcr", label: "Form CR (PDF)",            url: resolveUrl(item.pdf_formcr) },
        { key: "bundle", label: "Full Proof Bundle (PDF)",  url: resolveUrl(item.pdf_bundle) },
        { key: "slip",   label: "Acknowledgement Slip",     url: resolveUrl(item.slip) },
    ];
}

/* ─────────────────────────────────────────────────────────────────────────
   "View Documents" dropdown — lists every PDF available for a record.
   Closes on outside click. Disabled (greyed, no menu) when nothing has
   been generated yet, so the empty state is visible rather than silent.
───────────────────────────────────────────────────────────────────────── */
const DocumentsMenu = ({ item }) => {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    const docs = useMemo(() => getDocuments(item), [item]);
    const available = docs.filter((d) => d.url);

    useEffect(() => {
        if (!open) return;
        const onClickOutside = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", onClickOutside);
        return () => document.removeEventListener("mousedown", onClickOutside);
    }, [open]);

    if (available.length === 0) {
        return (
            <button className="btn btn-outline-secondary" disabled title="No documents generated yet">
                No documents yet
            </button>
        );
    }

    return (
        <div className="pan-docs-menu" ref={ref}>
            <button
                className="btn btn-outline-primary"
                onClick={() => setOpen((v) => !v)}
                aria-expanded={open}
            >
                View Documents ({available.length}) <span className="pan-docs-caret">▾</span>
            </button>
            {open && (
                <div className="pan-docs-dropdown" role="menu">
                    {docs.map((d) => (
                        <a
                            key={d.key}
                            href={d.url || undefined}
                            target="_blank"
                            rel="noreferrer"
                            role="menuitem"
                            className={`pan-docs-item ${d.url ? "" : "pan-docs-item-disabled"}`}
                            onClick={(e) => { if (!d.url) e.preventDefault(); }}
                        >
                            <span className="pan-docs-item-label">{d.label}</span>
                            <span className="pan-docs-item-status">
                                {d.url ? "Open ↗" : "Not generated"}
                            </span>
                        </a>
                    ))}
                </div>
            )}
        </div>
    );
};

const PanReports = () => {
    const [list, setList] = useState([]);
    const [loading, setLoading] = useState(false);

    const today = new Date().toISOString().slice(0, 10);
    const tomorrow = new Date(Date.now() + 86400000).toISOString().slice(0, 10);

    const [startDate, setStartDate] = useState(today);
    const [endDate, setEndDate] = useState(tomorrow);
    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");
    const { userid } = useSession();
    const navigate = useNavigate();
    const [showReceipt, setShowReceipt] = useState(false);
    const [panReceiptData, setPanReceiptData] = useState(null);

    const openReceipt = (apiData) => {
        setPanReceiptData({
            id: apiData.id,
            name: apiData.title + " " + apiData.name,
            aadhar: apiData.aadhar,
            mobile: apiData.mobile,
            fathername: apiData.fathername,
            dob: apiData.dob,
            email: apiData.email,
            gender: apiData.gender,
            status: apiData.status,
            created_at: apiData.created_date,
        });

        setShowReceipt(true);
    };

    useEffect(() => {
        fetchPanList();
    }, []);

    const fetchPanList = async () => {
        setLoading(true);
        try {
            const formData = new FormData();
            formData.append("action", "GET");
            formData.append("userid", userid);
            formData.append("filter_date", startDate);
            formData.append("end_date", endDate);
            const res = await fetchWrapper(
                "https://api.mokshiq.in/pan_action_manager",
                {
                    method: "POST",
                    body: formData
                }
            );

            setList(res?.data || []);
        } catch {
            ShowToast("Failed to fetch PAN data", "danger");
        } finally {
            setLoading(false);
        }
    };

    const filteredList = list.filter((item) => {
        const matchesSearch = item.aadhar?.toLowerCase().includes(search.toLowerCase());
        if (!matchesSearch) return false;
        if (statusFilter === "all") return true;
        return statusMeta(item.status).cls ===
            (statusFilter === "pending" ? "pending" : statusFilter === "accepted" ? "success" : "danger");
    });

    return (
        <main className="container-fluid ui-section">
            <div className="row m-1">
                <div className="col-10 ">
                    <h4 className="main-title">Pancard Service </h4>
                    <ul className="app-line-breadcrumbs mb-3">
                        <li>
                            <a className="f-s-14 f-w-500" href="#">
                                <span>
                                    <i className="ph-duotone ph-briefcase-metal f-s-16"></i>{" "}
                                    Services
                                </span>
                            </a>
                        </li>
                        <li className="active">
                            <a className="f-s-14 f-w-500" href="#">
                                Pancard Service
                            </a>
                        </li>
                    </ul>
                </div>
                <div className="col-2"> <button
                    className="add-btn"
                    onClick={() => navigate(`/pancard_apply`)}
                >
                    New PanCard Request
                </button></div>
            </div>

            {/* Filters */}
            <div className="row mb-3">
                <div className="col-md-2">
                    <input
                        type="date"
                        className="form-control"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                    />
                </div>
                <div className="col-md-2">
                    <input
                        type="date"
                        className="form-control"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                    />
                </div>
                <div className="col-md-2">
                    <select
                        className="form-select"
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                    >
                        {STATUS_FILTERS.map((s) => (
                            <option key={s.value} value={s.value}>{s.label}</option>
                        ))}
                    </select>
                </div>
                <div className="col-md-2">
                    <button className="btn btn-primary w-100" onClick={fetchPanList}>
                        Search
                    </button>
                </div>
                <div className="col-md-4">
                    <input
                        type="text"
                        className="form-control"
                        placeholder="Search by Aadhar"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                </div>
            </div>

            {/* Content */}
            {loading ? (
                <div className="loader">Loading...</div>
            ) : filteredList.length === 0 ? (
                <div className="empty-state">
                    No PAN records found for this date range, status, or search.
                </div>
            ) : (
                <div className="row">
                    {filteredList.map((item) => {
                        const meta = statusMeta(item.status);
                        return (
                            <div className="col-md-6 col-lg-4 mb-3" key={item.id}>
                                <div className="pan-card">
                                    <div className="pan-header">
                                        <h6 style={{ color: '#0b56f8' }}>
                                            {item.title} {item.name}
                                        </h6>
                                        <span style={{ color: 'white' }} className={`status ${meta.cls}`}>
                                            {meta.label}
                                        </span>
                                    </div>

                                    <div className="pan-body">
                                        <p><b>Aadhar:</b> {item.aadhar}</p>
                                        <p><b>Mobile:</b> {item.mobile}</p>
                                        <p><b>DOB:</b> {item.dob}</p>
                                        <p><b>Email:</b> {item.email}</p>
                                        <p><b>Gender:</b> {item.gender}</p>
                                    </div>

                                    <div className="pan-footer">
                                        <button
                                            className="btn btn-outline-primary"
                                            onClick={() => openReceipt(item)}
                                        >
                                            Print
                                        </button>

                                        <DocumentsMenu item={item} />
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
            <PanReceiptModal
                isOpen={showReceipt}
                receiptData={panReceiptData}
                onClose={() => setShowReceipt(false)}
                title="PAN Application Receipt"
                logoUrl="/assets/images/logo/2.png"
            />
        </main>
    );
};

export default PanReports;